import { useEffect, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { GUARDRAIL_TOPICS, DEFAULT_GUARDRAIL_MESSAGES } from 'librechat-data-provider';
import type { GuardrailMessages } from 'librechat-data-provider';
import { useCompanyPolicyQuery, useUpdateCompanyPolicyMutation } from './hooks';
import { Button, Muted, Notice, PageHeader, Panel, PanelHead } from './Chrome';
import { formatCount } from './format';
import Vocabulary from './Vocabulary';

/** Matches COMPANY_TEXT_MAX_LENGTH on the server. */
const TEXT_MAX = 8000;

export default function Settings({ groupId }: { groupId: string }): ReactElement | null {
  const { t } = useTranslation();
  const { data, isLoading } = useCompanyPolicyQuery(groupId);
  const save = useUpdateCompanyPolicyMutation();

  const [policy, setPolicy] = useState('');
  const [guidelines, setGuidelines] = useState('');
  const [messages, setMessages] = useState<GuardrailMessages>({});
  const [units, setUnits] = useState<string[]>([]);
  const [regions, setRegions] = useState<string[]>([]);
  const [managementLevels, setManagementLevels] = useState<string[]>([]);

  useEffect(() => {
    setPolicy(data?.policy ?? '');
    setGuidelines(data?.guidelines ?? '');
    setMessages(data?.guardrailMessages ?? {});
    setUnits(data?.units ?? []);
    setRegions(data?.regions ?? []);
    setManagementLevels(data?.managementLevels ?? []);
  }, [data]);

  if (isLoading) {
    return <Muted>{t('com_ui_leadhr_loading')}</Muted>;
  }

  const overLimit = [policy, guidelines, ...Object.values(messages)].some(
    (text) => (text?.length ?? 0) > TEXT_MAX,
  );

  const submit = () => {
    save.mutate({
      groupId,
      payload: {
        policy: policy.trim() ? policy : null,
        guidelines: guidelines.trim() ? guidelines : null,
        guardrailMessages: messages,
        units,
        regions,
        managementLevels,
      },
    });
  };

  return (
    <>
      <PageHeader
        title={t('com_ui_leadhr_settings_title')}
        subtitle={t('com_ui_leadhr_settings_subtitle')}
      />

      <Panel className="flex flex-col gap-4">
        <PanelHead
          title={t('com_ui_leadhr_guardrail_title')}
          subtitle={t('com_ui_leadhr_guardrail_help')}
        />
        <TextArea
          id="leadhr-policy"
          label={t('com_ui_leadhr_guardrail_title')}
          value={policy}
          onChange={setPolicy}
        />
      </Panel>

      <Panel className="flex flex-col gap-4">
        <PanelHead
          title={t('com_ui_leadhr_messages_title')}
          subtitle={t('com_ui_leadhr_messages_help')}
        />
        {GUARDRAIL_TOPICS.map((topic) => (
          <div key={topic} className="flex flex-col gap-2">
            <label htmlFor={`leadhr-message-${topic}`} className="text-sm font-semibold">
              {t(`com_ui_leadhr_message_${topic}`)}
            </label>
            <TextArea
              id={`leadhr-message-${topic}`}
              label={t(`com_ui_leadhr_message_${topic}`)}
              value={messages[topic] ?? ''}
              placeholder={DEFAULT_GUARDRAIL_MESSAGES[topic]}
              rows={4}
              onChange={(next) => setMessages((prev) => ({ ...prev, [topic]: next }))}
            />
          </div>
        ))}
      </Panel>

      <Panel className="flex flex-col gap-4">
        <PanelHead
          title={t('com_ui_leadhr_guidelines_title')}
          subtitle={t('com_ui_leadhr_guidelines_help')}
        />
        <TextArea
          id="leadhr-guidelines"
          label={t('com_ui_leadhr_guidelines_title')}
          value={guidelines}
          onChange={setGuidelines}
        />
      </Panel>

      <Panel className="flex flex-col gap-5">
        <PanelHead
          title={t('com_ui_leadhr_vocabulary_title')}
          subtitle={t('com_ui_leadhr_vocabulary_help')}
        />
        <Vocabulary
          label={t('com_ui_leadhr_col_unit')}
          values={units}
          onChange={setUnits}
          disabled={save.isLoading}
        />
        <Vocabulary
          label={t('com_ui_leadhr_col_region')}
          values={regions}
          onChange={setRegions}
          disabled={save.isLoading}
        />
        <Vocabulary
          label={t('com_ui_leadhr_col_level')}
          values={managementLevels}
          onChange={setManagementLevels}
          disabled={save.isLoading}
        />
      </Panel>

      <div className="flex flex-wrap items-center gap-3">
        <Button variant="primary" onClick={submit} disabled={save.isLoading || overLimit}>
          {save.isLoading ? t('com_ui_leadhr_saving') : t('com_ui_leadhr_save')}
        </Button>
        {save.isSuccess && !save.isLoading ? <Muted>{t('com_ui_leadhr_saved')}</Muted> : null}
        {save.isError ? <Notice>{t('com_ui_leadhr_save_failed')}</Notice> : null}
      </div>
    </>
  );
}

function TextArea({
  id,
  label,
  value,
  onChange,
  placeholder,
  rows = 6,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  rows?: number;
}): ReactElement {
  return (
    <div className="flex flex-col gap-1.5">
      <textarea
        id={id}
        aria-label={label}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        rows={rows}
        className="lh-input"
        style={{ background: 'var(--lh-offwhite)' }}
      />
      <span
        className="lh-small self-end"
        style={{ color: value.length > TEXT_MAX ? '#b00' : 'var(--lh-muted)' }}
      >
        {formatCount(value.length)}/{formatCount(TEXT_MAX)}
      </span>
    </div>
  );
}
